import { useState } from "react";
import { useGetAttendancePunchLog } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { fmtTime, fmtDay } from "@/components/attendance/attendance-list";

function ymd(d: Date): string {
  return new Date(d.getTime() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
}
const today = () => ymd(new Date());
const dayStartISO = (y: string) => new Date(`${y}T00:00:00+05:30`).toISOString();
const dayEndISO = (y: string) => new Date(`${y}T23:59:59+05:30`).toISOString();

// Raw biometric punch activity: how many times each person/PIN tapped per day,
// with the times. Biometric only (manual marks excluded).
export function PunchLog() {
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const { data, isLoading } = useGetAttendancePunchLog({
    dateFrom: dayStartISO(from),
    dateTo: dayEndISO(to),
  });
  const rows = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3 rounded-md border bg-card px-3 py-2.5">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Date range</p>
          <div className="flex items-center gap-2">
            <Input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="h-8 w-[150px]" />
            <span className="text-xs text-muted-foreground">→</span>
            <Input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="h-8 w-[150px]" />
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4">
          <p className="mb-3 text-xs text-muted-foreground">
            Raw biometric taps from the device — how many times each person punched and when. Manual
            "mark present" entries are <span className="font-medium">not</span> shown here.
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Day</TableHead>
                <TableHead>Person</TableHead>
                <TableHead>Taps</TableHead>
                <TableHead>Times</TableHead>
                <TableHead>First</TableHead>
                <TableHead>Last</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">No biometric punches in this range.</TableCell></TableRow>
              ) : (
                rows.map((r, i) => (
                  <TableRow key={`${r.day}-${r.pin}-${r.contactId ?? r.userId ?? "x"}-${i}`}>
                    <TableCell>{fmtDay(r.day)}</TableCell>
                    <TableCell>
                      {r.personName ? (
                        <div>
                          <span className="font-medium">{r.personName}</span>
                          {r.accountName ? <span className="ml-1 text-xs text-muted-foreground">· {r.accountName}</span> : null}
                          {r.personType === "staff" ? <Badge variant="outline" className="ml-1">Staff</Badge> : null}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">
                          PIN {r.pin} <Badge variant="outline" className="ml-1 text-amber-600">unassigned</Badge>
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums font-medium">{r.taps}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {(r.times ?? []).map((t) => fmtTime(t)).join(", ")}
                    </TableCell>
                    <TableCell className="tabular-nums">{fmtTime(r.firstAt)}</TableCell>
                    <TableCell className="tabular-nums">{fmtTime(r.lastAt)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
